import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useScreenDimensions, useDevice } from "../_layout";

import {
  Text,
  View,
  TouchableOpacity,
  Animated,
  Dimensions,
} from "react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { useTranslation } from "react-i18next";
import { Feather } from "@expo/vector-icons";

import { observer, Observer } from "mobx-react";

import ChatMediaPlayer from "./ChatMediaPlayer";
import ChatView from "./ChatView";
import CustomAlert from "../Components/CustomAlert";
import ChatManager from "../Classes/ChatManager";
import MediaManager from "../Classes/MediaManager";
import { Chat } from "../../Resources/Constants";
import AudioManager from "../Classes/AudioManager";
import ChatListView from "./ChatListView";
import ClientNameLogo from "./ClientNameLogo";
import Tooltip from "../Components/Tooltip";
import {
  ChatHoverPresentationMode,
  getChatHoverPresentationMode,
} from "./chatHoverPresentation";
import {
  clampChatHoverTranslateX,
  getChatHoverDragBounds,
  getChatHoverDragThreshold,
  getChatHoverDragTranslateX,
  shouldActivateChatHoverDrag,
} from "./chatHoverDragUtils";

const getPointerPoint = (event) => {
  const nativeEvent = event?.nativeEvent ?? event;
  const touch =
    nativeEvent?.touches?.[0] ?? nativeEvent?.changedTouches?.[0] ?? null;
  const clientX = touch?.clientX ?? nativeEvent?.clientX;
  const clientY = touch?.clientY ?? nativeEvent?.clientY;

  if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
    return null;
  }

  return { x: clientX, y: clientY };
};

const getPointerType = (event) => {
  const nativeEvent = event?.nativeEvent ?? event;

  if (nativeEvent?.pointerType) {
    return nativeEvent.pointerType;
  }

  if (nativeEvent?.touches || nativeEvent?.changedTouches) {
    return "touch";
  }

  return "mouse";
};

const getPointerId = (event) => {
  const nativeEvent = event?.nativeEvent ?? event;
  return Number.isFinite(nativeEvent?.pointerId) ? nativeEvent.pointerId : null;
};

const isPrimaryMouseButton = (event) => {
  const nativeEvent = event?.nativeEvent ?? event;
  const pointerType = getPointerType(event);

  if (pointerType !== "mouse") {
    return true;
  }

  return nativeEvent?.button == null || nativeEvent.button === 0;
};

const ChatHover = observer(({ coach, keyboardHeight = 0 }) => {
  const { screenSize } = useScreenDimensions();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const chatManager = ChatManager.getInstance();
  const mediaManager = MediaManager.getInstance();
  const audioManager = AudioManager.getInstance();
  const [width, setWidth] = useState(() => Dimensions.get("window").width);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [panelWidth, setPanelWidth] = useState(0);
  const [chatDragIsActive, setChatDragIsActive] = useState(false);
  const dragTranslateX = useRef(new Animated.Value(0)).current;
  const dragHandleRef = useRef(null);
  const dragSessionRef = useRef(null);
  const dragBoundsRef = useRef({ minX: 0, maxX: 0 });
  const committedTranslateXRef = useRef(0);
  const latestTranslateXRef = useRef(0);
  const removeDragListenersRef = useRef(null);
  const restoreBodyStylesRef = useRef(null);
  const pointerCaptureRef = useRef(null);

  useEffect(() => {
    const onChange = ({ window: { width: newWidth } }) => {
      setWidth((prev) => (prev !== newWidth ? newWidth : prev));
    };
    const subscription = Dimensions.addEventListener("change", onChange);
    return () => {
      if (subscription?.remove) {
        subscription.remove();
      }
    };
  }, []);

  const effectiveChatIsExpanded = isMobile ? true : coach.chatIsExpanded;

  const chatWidth =
    effectiveChatIsExpanded && coach.showingChatList
      ? Chat.CHAT_WIDTH_EXPANDED
      : Chat.CHAT_WIDTH;

  const viewMargin = screenSize != "small" ? 24 : 0;
  const effectivePanelWidth = panelWidth > 0 ? panelWidth : chatWidth;
  const dragBounds = useMemo(
    () =>
      getChatHoverDragBounds({
        viewportWidth: width,
        panelWidth: effectivePanelWidth,
        viewMargin,
      }),
    [width, effectivePanelWidth, viewMargin],
  );
  const presentationMode = getChatHoverPresentationMode({
    isMobile,
    showingChatList: coach.showingChatList,
    chatIsExpanded: effectiveChatIsExpanded,
    screenSize,
  });

  const cleanupDragListeners = useCallback(() => {
    if (removeDragListenersRef.current) {
      removeDragListenersRef.current();
      removeDragListenersRef.current = null;
    }
  }, []);

  const restoreBodyDragStyles = useCallback(() => {
    if (restoreBodyStylesRef.current) {
      restoreBodyStylesRef.current();
      restoreBodyStylesRef.current = null;
    }
  }, []);

  const releasePointerCapture = useCallback(() => {
    const pointerCapture = pointerCaptureRef.current;
    pointerCaptureRef.current = null;

    if (!pointerCapture) {
      return;
    }

    try {
      pointerCapture.node?.releasePointerCapture?.(pointerCapture.pointerId);
    } catch (error) {
      // The browser can release capture itself before our cleanup runs.
    }
  }, []);

  const finishChatDrag = useCallback(
    ({ commit = true, updateState = true } = {}) => {
      cleanupDragListeners();
      releasePointerCapture();
      restoreBodyDragStyles();
      chatManager.setProperty("isDragging", false);

      const nextTranslateX = commit
        ? clampChatHoverTranslateX(
            latestTranslateXRef.current,
            dragBoundsRef.current,
          )
        : committedTranslateXRef.current;

      latestTranslateXRef.current = nextTranslateX;
      committedTranslateXRef.current = nextTranslateX;
      dragTranslateX.setValue(nextTranslateX);
      dragSessionRef.current = null;

      if (updateState) {
        setChatDragIsActive(false);
      }
    },
    [
      chatManager,
      cleanupDragListeners,
      dragTranslateX,
      releasePointerCapture,
      restoreBodyDragStyles,
    ],
  );

  const applyBodyDragStyles = useCallback(() => {
    if (restoreBodyStylesRef.current || typeof document === "undefined") {
      return;
    }

    const body = document.body;
    if (!body?.style) {
      return;
    }

    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;
    const previousWebkitUserSelect = body.style.webkitUserSelect;

    body.style.cursor = "grabbing";
    body.style.userSelect = "none";
    body.style.webkitUserSelect = "none";

    restoreBodyStylesRef.current = () => {
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
      body.style.webkitUserSelect = previousWebkitUserSelect;
    };
  }, []);

  const activateChatDrag = useCallback(
    (session) => {
      if (session.isActive) {
        return;
      }

      session.isActive = true;
      chatManager.setProperty("isDragging", true);
      setChatDragIsActive(true);
      applyBodyDragStyles();
    },
    [applyBodyDragStyles, chatManager],
  );

  const handleDragMove = useCallback(
    (event) => {
      const session = dragSessionRef.current;
      if (!session) {
        return;
      }

      const pointerId = getPointerId(event);
      if (session.pointerId != null && pointerId !== session.pointerId) {
        return;
      }

      const point = getPointerPoint(event);
      if (!point) {
        return;
      }

      const deltaX = point.x - session.startClientX;
      const deltaY = point.y - session.startClientY;

      if (!session.isActive) {
        if (
          !shouldActivateChatHoverDrag({
            deltaX,
            deltaY,
            threshold: session.threshold,
          })
        ) {
          return;
        }

        activateChatDrag(session);
      }

      event?.preventDefault?.();
      const nextTranslateX = getChatHoverDragTranslateX({
        startTranslateX: session.startTranslateX,
        startClientX: session.startClientX,
        clientX: point.x,
        bounds: dragBoundsRef.current,
      });

      latestTranslateXRef.current = nextTranslateX;
      dragTranslateX.setValue(nextTranslateX);
    },
    [activateChatDrag, dragTranslateX],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const session = dragSessionRef.current;
      if (!session) {
        return;
      }

      const pointerId = getPointerId(event);
      if (session.pointerId != null && pointerId !== session.pointerId) {
        return;
      }

      finishChatDrag();
    },
    [finishChatDrag],
  );

  const handleDragCancel = useCallback(
    (event) => {
      const session = dragSessionRef.current;
      if (!session) {
        return;
      }

      const pointerId = getPointerId(event);
      if (session.pointerId != null && pointerId !== session.pointerId) {
        return;
      }

      finishChatDrag();
    },
    [finishChatDrag],
  );

  const handleWindowBlur = useCallback(() => {
    finishChatDrag();
  }, [finishChatDrag]);

  const handleVisibilityChange = useCallback(() => {
    if (typeof document !== "undefined" && document.hidden) {
      finishChatDrag();
    }
  }, [finishChatDrag]);

  const attachDragListeners = useCallback(
    ({ mode, targetWindow, targetDocument, captureNode }) => {
      cleanupDragListeners();

      const moveOptions = { passive: false };
      if (mode === "touch") {
        targetWindow.addEventListener("touchmove", handleDragMove, moveOptions);
        targetWindow.addEventListener("touchend", handleDragEnd);
        targetWindow.addEventListener("touchcancel", handleDragCancel);
      } else if (mode === "mouse") {
        targetWindow.addEventListener(
          "mousemove",
          handleDragMove,
          moveOptions,
        );
        targetWindow.addEventListener("mouseup", handleDragEnd);
      } else {
        targetWindow.addEventListener(
          "pointermove",
          handleDragMove,
          moveOptions,
        );
        targetWindow.addEventListener("pointerup", handleDragEnd);
        targetWindow.addEventListener("pointercancel", handleDragCancel);
        captureNode?.addEventListener?.("lostpointercapture", handleDragCancel);
      }

      targetWindow.addEventListener("blur", handleWindowBlur);
      targetDocument?.addEventListener?.(
        "visibilitychange",
        handleVisibilityChange,
      );

      removeDragListenersRef.current = () => {
        if (mode === "touch") {
          targetWindow.removeEventListener("touchmove", handleDragMove);
          targetWindow.removeEventListener("touchend", handleDragEnd);
          targetWindow.removeEventListener("touchcancel", handleDragCancel);
        } else if (mode === "mouse") {
          targetWindow.removeEventListener("mousemove", handleDragMove);
          targetWindow.removeEventListener("mouseup", handleDragEnd);
        } else {
          targetWindow.removeEventListener("pointermove", handleDragMove);
          targetWindow.removeEventListener("pointerup", handleDragEnd);
          targetWindow.removeEventListener("pointercancel", handleDragCancel);
          captureNode?.removeEventListener?.(
            "lostpointercapture",
            handleDragCancel,
          );
        }

        targetWindow.removeEventListener("blur", handleWindowBlur);
        targetDocument?.removeEventListener?.(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    },
    [
      cleanupDragListeners,
      handleDragCancel,
      handleDragEnd,
      handleDragMove,
      handleVisibilityChange,
      handleWindowBlur,
    ],
  );

  const startChatDrag = useCallback(
    (event) => {
      if (!isPrimaryMouseButton(event)) {
        return;
      }

      const point = getPointerPoint(event);
      if (!point || dragSessionRef.current) {
        return;
      }

      const nativeEvent = event?.nativeEvent ?? event;
      const pointerType = getPointerType(event);
      const pointerId = getPointerId(event);
      const captureNode = event?.currentTarget ?? dragHandleRef.current;
      const ownerDocument =
        captureNode?.ownerDocument ??
        (typeof document !== "undefined" ? document : null);
      const targetWindow =
        ownerDocument?.defaultView ??
        (typeof window !== "undefined" ? window : null);

      if (!targetWindow) {
        return;
      }

      event?.preventDefault?.();
      event?.stopPropagation?.();

      const startTranslateX = clampChatHoverTranslateX(
        committedTranslateXRef.current,
        dragBoundsRef.current,
      );
      committedTranslateXRef.current = startTranslateX;
      latestTranslateXRef.current = startTranslateX;
      dragTranslateX.setValue(startTranslateX);

      dragSessionRef.current = {
        isActive: false,
        pointerId,
        pointerType,
        startClientX: point.x,
        startClientY: point.y,
        startTranslateX,
        threshold: getChatHoverDragThreshold(pointerType),
      };

      if (pointerId != null && captureNode?.setPointerCapture) {
        try {
          captureNode.setPointerCapture(pointerId);
          pointerCaptureRef.current = { node: captureNode, pointerId };
        } catch (error) {
          pointerCaptureRef.current = null;
        }
      }

      const mode =
        nativeEvent?.type === "touchstart"
          ? "touch"
          : nativeEvent?.type === "mousedown"
            ? "mouse"
            : "pointer";

      attachDragListeners({
        mode,
        targetWindow,
        targetDocument: ownerDocument,
        captureNode,
      });
    },
    [attachDragListeners, dragTranslateX],
  );

  const dragHandleProps = useMemo(() => {
    if (typeof window !== "undefined" && "PointerEvent" in window) {
      return { onPointerDown: startChatDrag };
    }

    return {
      onMouseDown: startChatDrag,
      onTouchStart: startChatDrag,
    };
  }, [startChatDrag]);

  const handlePanelLayout = useCallback((event) => {
    const nextPanelWidth = event?.nativeEvent?.layout?.width;
    if (!Number.isFinite(nextPanelWidth) || nextPanelWidth <= 0) {
      return;
    }

    setPanelWidth((previousPanelWidth) =>
      previousPanelWidth !== nextPanelWidth
        ? nextPanelWidth
        : previousPanelWidth,
    );
  }, []);

  useEffect(() => {
    dragBoundsRef.current = dragBounds;
    const nextTranslateX = clampChatHoverTranslateX(
      committedTranslateXRef.current,
      dragBounds,
    );

    committedTranslateXRef.current = nextTranslateX;
    latestTranslateXRef.current = nextTranslateX;
    dragTranslateX.setValue(nextTranslateX);
  }, [dragBounds, dragTranslateX]);

  useEffect(() => {
    if (presentationMode !== ChatHoverPresentationMode.PANEL) {
      finishChatDrag();
    }
  }, [finishChatDrag, presentationMode]);

  useEffect(() => {
    return () => {
      finishChatDrag({ updateState: false });
    };
  }, [finishChatDrag]);

  const TopBarRow = observer(() => {
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          height: 52,
          borderBottomWidth: coach.selectedChatClient ? 0.1 : 0,
          borderColor: Colors.borderColor,
        }}
      >
        {coach.selectedChatClient ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <TouchableOpacity
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
              disabled={audioManager.isRecording}
              onPress={() => {
                if (coach.showingChatList) {
                  coach.setProperty("selectedChatId", "");
                  chatManager.setProperty("selectedReplyToId", null);
                  mediaManager.closeMediaPlayer();
                } else {
                  coach.setProperty("showingChatList", true);
                }
              }}
            >
              <Feather
                name="chevron-left"
                size={24}
                color="black"
                style={{ paddingLeft: 4 }}
              />
              <ClientNameLogo
                client={coach.selectedChatClient}
                isForChat={true}
              />
            </TouchableOpacity>
            <View>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text style={{ fontWeight: "600", fontSize: 14 }}>
                  {coach.selectedChatClient.nameForCoach}
                </Text>
                {coach.selectedClientId !== coach.selectedChatClient.id && (
                  <Tooltip
                    delayedOpen={true}
                    content="openAthlete-string"
                    placement="bottom"
                  >
                    <TouchableOpacity
                      onPress={() => {
                        coach.selectClient(
                          coach.selectedChatClient.id,
                          t,
                          i18n,
                        );
                      }}
                      onMouseEnter={() =>
                        !isMobile && setHoveredButton("openAthlete")
                      }
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{ marginLeft: 4 }}
                    >
                      <Feather
                        name="arrow-up-right"
                        size={14}
                        color={
                          hoveredButton === "openAthlete"
                            ? Colors.darkgrayColor
                            : Colors.grayColor
                        }
                      />
                    </TouchableOpacity>
                  </Tooltip>
                )}
              </View>
              <Text style={{ fontSize: 10, color: Colors.grayColor }}>
                {coach.selectedChatClient.email}
              </Text>
            </View>
          </View>
        ) : (
          <TouchableOpacity
            onPress={() =>
              coach.setProperty("showingChatList", !coach.showingChatList)
            }
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            <Text
              style={{
                fontWeight: "600",
                fontSize: 16,
                paddingLeft: 16,
              }}
            >
              {t("chats-string")}
            </Text>
            {coach.totalNewChats > 0 && (
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text
                  style={{
                    color: Colors.bubbleColor,
                    backgroundColor: "rgba(0, 0, 0, 0.07)",
                    borderRadius: 12,
                    alignSelf: "center",
                    textAlign: "center",
                    textAlignVertical: "center",
                    fontSize: 11,
                    width: 20,
                    minWidth: 20, //To avoid being compressed when preview text is large
                    height: 20,
                    lineHeight: 20,
                    marginLeft: 8,
                    // borderColor: Colors.grayColor,
                    // borderWidth: 0.5,
                  }}
                >
                  {coach.totalNewChats <= 99 ? coach.totalNewChats : 99}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        )}

        <View
          ref={dragHandleRef}
          {...dragHandleProps}
          accessibilityLabel="Move chat panel"
          style={{
            flex: 1,
            minWidth: 48,
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            cursor: chatDragIsActive ? "grabbing" : "grab",
            touchAction: "none",
            userSelect: "none",
          }}
        />
        {!isMobile && (
          <Tooltip
            delayedOpen={true}
            content={
              coach.chatIsExpanded
                ? "minimizeChat-string"
                : "expandChat-string"
            }
            placement="bottom"
          >
            <TouchableOpacity
              onPress={() => {
                if (!coach.chatIsExpanded) {
                  coach.setProperty("chatIsExpanded", true);
                  coach.setProperty("showingChatList", true);
                } else {
                  coach.setProperty("chatIsExpanded", false);
                  coach.setProperty("showingChatList", true);
                }
              }}
            >
              <Feather
                name={coach.chatIsExpanded ? "minimize-2" : "maximize-2"}
                size={20}
                color={Colors.blackColor}
                style={{
                  paddingRight: 16,
                }}
              />
            </TouchableOpacity>
          </Tooltip>
        )}

        <Tooltip
          delayedOpen={true}
          content={
            coach.showingChatList ? "hideChat-string" : "showChat-string"
          }
          placement="bottom"
        >
          <TouchableOpacity
            onPress={() =>
              coach.setProperty("showingChatList", !coach.showingChatList)
            }
          >
            <Feather
              name={coach.showingChatList ? "chevrons-down" : "chevrons-up"}
              size={24}
              color={Colors.blackColor}
              style={{
                paddingRight: 16,
              }}
            />
          </TouchableOpacity>
        </Tooltip>
      </View>
    );
  });

  if (presentationMode === ChatHoverPresentationMode.HIDDEN_EXPANDED_DESKTOP) {
    return null;
  }

  if (presentationMode === ChatHoverPresentationMode.MOBILE_PEEK) {
    return (
      <>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={t("showChat-string")}
          onPress={() => coach.setProperty("showingChatList", true)}
          activeOpacity={0.8}
          style={{
            position: "absolute",
            right: 0,
            bottom: keyboardHeight + 72,
            zIndex: 100,
            width: 36,
            height: 56,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            backgroundColor: Colors.whiteColor,
            borderWidth: 0.1,
            borderColor: Colors.borderColor,
            borderRightWidth: 0,
            alignItems: "flex-start",
            justifyContent: "center",
            paddingLeft: 7,
            shadowColor: "#000",
            shadowOpacity: 0.14,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
            elevation: 4,
          }}
        >
          <Feather name="message-circle" size={20} color={Colors.blackColor} />
          {coach.totalNewChats > 0 && (
            <View
              style={{
                position: "absolute",
                top: 7,
                left: 16,
                backgroundColor: Colors.bubbleColor,
                borderRadius: 8,
                minWidth: 15,
                height: 15,
                alignItems: "center",
                justifyContent: "center",
                paddingHorizontal: 3,
              }}
            >
              <Text
                style={{
                  color: Colors.whiteColor,
                  fontSize: 8,
                  fontWeight: "700",
                }}
              >
                {coach.totalNewChats <= 99 ? coach.totalNewChats : 99}
              </Text>
            </View>
          )}
        </TouchableOpacity>

        <Observer>
          {() =>
            chatManager.showingPermissionsAlert && (
              <CustomAlert
                isVisible={chatManager.showingPermissionsAlert}
                onClose={() =>
                  chatManager.setProperty("showingPermissionsAlert", false)
                }
                title={"permissionsAlertTitle-string"}
                text={"permissionsAlertText-string"}
                noTranslateText={true}
              />
            )
          }
        </Observer>
      </>
    );
  }

  return (
    <>
      <Animated.View
        pointerEvents="box-none"
        onLayout={handlePanelLayout}
        style={[
          {
            width: chatWidth,
            position: "absolute",
            bottom: keyboardHeight,
            right: screenSize !== "small" ? 24 : 0,
            zIndex: 100,
            maxHeight: `calc(100% - ${keyboardHeight}px)`,
            justifyContent: "flex-end",
            alignItems: "center",
            paddingTop: 8,
            maxWidth: "100%",
            transform: [{ translateX: dragTranslateX }],
          },
        ]}
      >
        {mediaManager.showingMediaPlayer && <ChatMediaPlayer coach={coach} />}
        <View
          style={{
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: "#fff",
            borderWidth: 0.1,
            borderColor: "#ccc",
            width: "100%",
            maxHeight: coach.showingChatList
              ? effectiveChatIsExpanded
                ? "100%"
                : 620
              : undefined,
            minHeight: coach.showingChatList ? 220 : 52,
            flexShrink: 100,
          }}
        >
          <TopBarRow />
          {coach.showingChatList && (
            <View style={{ flex: 1, paddingBottom: 0 }}>
              {coach.selectedChatClient ? (
                <ChatView client={coach.selectedChatClient} />
              ) : (
                <>
                  <ChatListView coach={coach} />
                </>
              )}
            </View>
          )}
        </View>
      </Animated.View>

      <Observer>
        {() =>
          chatManager.showingPermissionsAlert && (
            <CustomAlert
              isVisible={chatManager.showingPermissionsAlert}
              onClose={() =>
                chatManager.setProperty("showingPermissionsAlert", false)
              }
              title={"permissionsAlertTitle-string"}
              text={"permissionsAlertText-string"}
              noTranslateText={true}
            />
          )
        }
      </Observer>
    </>
  );
});

export default ChatHover;
